import React from "react";
import { motion } from "framer-motion";
import { useMicrospecThemeLanguageContext } from "../../../MicrospecDynamicUI/Themes";
import { Box, Typography, Container, Grid } from "@mui/material";
import BiotechIcon from "@mui/icons-material/Biotech";
import TuneIcon from "@mui/icons-material/Tune";
import InsightsIcon from "@mui/icons-material/Insights";

const stepIcons = [BiotechIcon, TuneIcon, InsightsIcon];
const stepColors = ["#7393B3", "#8B5CF6", "#C084FC"];

const ServicesSection = () => {
  const { localizationProvider, dir } = useMicrospecThemeLanguageContext();

  const sectionLabel = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.services.sectionLabel",
  });
  const title = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.services.title",
  });
  const subtitle = localizationProvider.translateByType({
    type: "Localized",
    text: "landing.services.subtitle",
  });

  const services = [0, 1, 2].map((i) => ({
    title: localizationProvider.translateByType({
      type: "Localized",
      text: `landing.services.items.${i}.title`,
    }),
    desc: localizationProvider.translateByType({
      type: "Localized",
      text: `landing.services.items.${i}.description`,
    }),
    step: `0${i + 1}`,
    Icon: stepIcons[i],
    color: stepColors[i],
  }));

  return (
    <Box className="py-28 relative overflow-hidden" id="how-it-works" sx={{ background: "transparent" }}>
      {/* Atmospheric blobs */}
      <motion.div
        className="absolute top-[10%] left-[-10%] w-[40%] h-[50%] rounded-full bg-[#D4C5F9]/30 blur-[130px] pointer-events-none"
        animate={{ scale: [1, 1.06, 1], x: [0, 20, 0] }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-[0%] right-[-10%] w-[35%] h-[45%] rounded-full bg-[#F5E1EC]/25 blur-[110px] pointer-events-none"
        animate={{ scale: [1, 1.08, 1] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 5 }}
      />

      <Container maxWidth="xl" dir={dir}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 px-4"
        >
          <Typography
            variant="overline"
            className="font-bold tracking-[0.2em] mb-3 block"
            sx={{
              background: "linear-gradient(135deg, #7393B3, #8B5CF6)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {sectionLabel}
          </Typography>
          <Typography
            variant="h2"
            className="text-4xl md:text-5xl font-bold tracking-tight text-[#2D1B4E] mb-4"
          >
            {title}
          </Typography>
          <Typography className="text-lg text-[#6B5B7B] max-w-2xl mx-auto">
            {subtitle}
          </Typography>
        </motion.div>

        <div className="relative px-4 md:px-8">
          {/* Glowing connection line (desktop only) */}
          <div className="hidden md:block absolute top-[44px] left-[calc(16.67%+36px)] right-[calc(16.67%+36px)] h-[2px] z-0">
            <motion.div
              className="w-full h-full rounded-full"
              style={{
                background: "linear-gradient(90deg, #7393B3, #8B5CF6, #C084FC)",
                boxShadow: "0 0 12px rgba(139,92,246,0.2)",
              }}
              initial={{
                scaleX: 0,
                transformOrigin: dir === "rtl" ? "right" : "left",
              }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2, delay: 0.3 }}
            />
          </div>

          {/* Animated glowing dots on the line */}
          {[0, 1].map((i) => (
            <motion.div
              key={`dot-${i}`}
              className="hidden md:block absolute top-[40px] w-3 h-3 rounded-full z-[1]"
              style={{
                left: `${33.33 + i * 33.33}%`,
                background: "linear-gradient(135deg, #8B5CF6, #C084FC)",
                boxShadow: "0 0 12px rgba(139,92,246,0.4)",
              }}
              initial={{ scale: 0 }}
              whileInView={{ scale: [0, 1.5, 1] }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.6 + i * 0.3 }}
            />
          ))}

          <Grid container spacing={6}>
            {services.map((service, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="flex flex-col items-center text-center relative z-10"
                >
                  {/* Step circle with frosted glass and glow */}
                  <div className="relative mb-8">
                    {/* Outer pulsing ring */}
                    <motion.div
                      className="absolute inset-[-10px] rounded-full"
                      style={{
                        border: `2px solid ${service.color}`,
                        opacity: 0.2,
                        boxShadow: `0 0 20px ${service.color}30`,
                      }}
                      animate={{ scale: [1, 1.25, 1], opacity: [0.2, 0, 0.2] }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        delay: index * 0.5,
                      }}
                    />
                    <div
                      className="w-[72px] h-[72px] rounded-full flex items-center justify-center border-4 border-white/60 relative backdrop-blur-xl"
                      style={{
                        background: `linear-gradient(135deg, rgba(255,255,255,0.6), rgba(255,255,255,0.3))`,
                        boxShadow: `0 8px 30px ${service.color}18, 0 0 0 1px rgba(255,255,255,0.5) inset`,
                      }}
                    >
                      <service.Icon
                        sx={{ fontSize: 30, color: service.color }}
                      />
                    </div>
                  </div>

                  {/* Step label */}
                  <Typography
                    className="text-xs font-bold tracking-[0.3em] uppercase mb-3"
                    style={{ color: service.color }}
                  >
                    Step {service.step}
                  </Typography>

                  {/* Title */}
                  <Typography
                    variant="h5"
                    className="font-bold text-[#2D1B4E] tracking-tight mb-3"
                  >
                    {service.title}
                  </Typography>

                  {/* Description */}
                  <Typography className="text-[#6B5B7B] leading-relaxed max-w-sm text-[15px]">
                    {service.desc}
                  </Typography>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </div>
      </Container>
    </Box>
  );
};

export default ServicesSection;
